import React, { useState } from 'react'
import axios from 'axios'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import { Download } from '@dsny/dsny-component-library/icons'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { useDispatch } from 'react-redux'
import { MaterialIcon, Tonality } from '@dsny/dsny-component-library'
import { thunks as playQueueThunks } from 'reducers/PlayQueue'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import useReleaseInfoMetadataInTracklist from 'utils/hooks/useReleaseInfoMetadataInTracklist'
import styles from './Tabs.module.scss'
import TrackList from '../TrackList/TrackList'
import DownloadMetaData from '../../../../components/Dropdowns/DownloadMetaData/DownloadMetaData'
import AttachmentList from '../Attachments/AttachmentList'
import useReleaseInformationResponsiveBreakpointsV2 from '../../hooks/useReleaseInformationResponsiveBreakpointsV2'

export default function InfoTabs({ release }) {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()
  const [isAllMetaDataTracksExpanded, setIsAllMetaDataTracksExpanded] =
    useState(false)
  const isMetadataInTracklistEnabled = useReleaseInfoMetadataInTracklist()
  const { songs, markettags, attachments } = release
  const isCanadianContent = markettags?.includes('cancon')
  const handlePlayRelease = () => {
    dispatch(
      playQueueThunks.playRelease(release?.releaseid, {
        source: PLAY_SOURCES.RELEASE_INFORMATION,
        trigger: PLAY_TRIGGERS.PLAY_ALL
      })
    )
  }

  const toggleAll = () => {
    setIsAllMetaDataTracksExpanded(!isAllMetaDataTracksExpanded)
  }

  const downloadAllAttachments = async () => {
    await Promise.all(
      attachments.map(async (attachment, i) => {
        try {
          const response = await axios({
            method: 'get',
            url: attachment?.url,
            responseType: 'blob' // to handle binary files
          })

          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')

          link.href = url
          link.setAttribute('download', `${attachment.title}`)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } catch (error) {
          console.log(`Error downloading attachment ${i}:`, error)
        }
      })
    )
  }

  return (
    <Tabs
      className={classNames(
        styles.infoTabs,
        styles.legacyInfoTabs,
        responsiveBreakpointsV2 && styles.responsiveBreakpointsV2
      )}
    >
      <TabList className={styles.tabList}>
        <Tab className={styles.tab} selectedClassName={styles.tabSelected}>
          {t('release-info.tracklist')}
        </Tab>
        {!isMetadataInTracklistEnabled && (
          <Tab className={styles.tab} selectedClassName={styles.tabSelected}>
            {t('release-info.metadata')}
          </Tab>
        )}
        <Tab className={styles.tab} selectedClassName={styles.tabSelected}>
          {t('release-info.attachments')}
        </Tab>
      </TabList>

      <TabPanel
        className={styles.tabPanel}
        selectedClassName={styles.tabPanelSelected}
      >
        <div className={styles.trackListHeader}>
          <strong className={styles.tabPanelTitle}>{`Tracks (${
            songs?.length || 0
          })`}</strong>
          <div className={styles.trackListIcons}>
            <Tonality.Pill
              variant="outlined"
              mode="dark"
              label={t('release-info.play-all')}
              icon={
                <MaterialIcon
                  name="PlayArrow"
                  color={theme.colors.neutralW90}
                />
              }
              style={{
                marginLeft: 'auto'
              }}
              onClick={handlePlayRelease}
            />
            {isMetadataInTracklistEnabled && (
              <>
                <DownloadMetaData release={release} />
                <Tonality.Pill
                  variant="outlined"
                  mode="dark"
                  label={
                    isAllMetaDataTracksExpanded
                      ? t('release-info.collapse-all')
                      : t('release-info.expand-all')
                  }
                  style={{ marginLeft: 'auto' }}
                  onClick={toggleAll}
                />
              </>
            )}
          </div>
        </div>
        <div
          className={classNames(
            styles.trackListContent,
            styles.trackListContentScrollable
          )}
        >
          <TrackList
            showMetadata={isMetadataInTracklistEnabled}
            songs={songs}
            isCanadianContent={isCanadianContent}
            release={release}
            isAllMetaDataTracksExpanded={isAllMetaDataTracksExpanded}
          />
        </div>
      </TabPanel>
      {!isMetadataInTracklistEnabled && (
        <TabPanel
          className={styles.tabPanel}
          selectedClassName={styles.tabPanelSelected}
        >
          <div className={styles.trackListHeader}>
            <strong className={styles.tabPanelTitle}>{`Track details (${
              songs?.length || 0
            })`}</strong>
            <div className={styles.trackListIcons}>
              <DownloadMetaData release={release} />
              <Tonality.Pill
                variant="outlined"
                mode="dark"
                label={
                  isAllMetaDataTracksExpanded
                    ? t('release-info.collapse-all')
                    : t('release-info.expand-all')
                }
                style={{ marginLeft: 'auto' }}
                onClick={toggleAll}
              />
            </div>
          </div>
          <div
            className={classNames(
              styles.trackListContent,
              styles.trackListContentScrollable
            )}
          >
            <TrackList
              metaData
              songs={songs}
              isCanadianContent={isCanadianContent}
              release={release}
              isAllMetaDataTracksExpanded={isAllMetaDataTracksExpanded}
            />
          </div>
        </TabPanel>
      )}
      <TabPanel
        className={styles.tabPanel}
        selectedClassName={styles.tabPanelSelected}
      >
        <div className={styles.trackListHeader}>
          <strong className={styles.tabPanelTitle}>{`${t(
            'release-info.additional-files'
          )} (${attachments?.length || 0})`}</strong>
          <Tonality.Pill
            variant="outlined"
            mode="dark"
            label="Download all"
            icon={<Download />}
            disabled={attachments?.length === 0}
            style={{
              marginLeft: 'auto',
              width: 'fit-content',
              padding: '4px 6px'
            }}
            onClick={() => downloadAllAttachments(attachments)}
          />
        </div>
        <div className={styles.trackListContent}>
          <AttachmentList attachments={attachments} />
        </div>
      </TabPanel>
    </Tabs>
  )
}

InfoTabs.propTypes = {
  release: PropTypes.shape({
    releaseid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    songs: PropTypes.arrayOf(PropTypes.shape({})),
    markettags: PropTypes.arrayOf(PropTypes.string),
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string
      })
    )
  }).isRequired
}
